// Mobile-specific utilities using Capacitor
// These functions work in both web and mobile contexts

// Check if running on mobile device
export const isMobile = () => {
  return typeof window !== 'undefined' && 
         (window.Capacitor || window.cordova || 
          navigator.userAgent.includes('Capacitor') || 
          navigator.userAgent.includes('Cordova'));
};

// Legacy support
export const isMobileDevice = isMobile;

// Get platform info
export const getPlatform = async () => {
  if (!isMobile()) return 'web';
  
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.getPlatform();
  } catch (error) {
    return 'web';
  }
};

// Initialize mobile app
export const initializeMobileApp = async () => {
  if (!isMobile()) return;

  try {
    const [
      { SplashScreen },
      { StatusBar, Style },
      { App },
      { Network }
    ] = await Promise.all([
      import('@capacitor/splash-screen'),
      import('@capacitor/status-bar'),
      import('@capacitor/app'),
      import('@capacitor/network')
    ]);

    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Set status bar style
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#e74c3c' });

    // Listen for app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active?', isActive);
    });

    // Listen for network changes
    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
    });

  } catch (error) {
    console.error('Error initializing mobile app:', error);
  }
};

// Haptic feedback (primary function)
export const hapticFeedback = async (style = 'Medium') => {
  if (!isMobile()) return;
  
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle[style] || ImpactStyle.Medium });
  } catch (error) {
    console.error('Haptic feedback error:', error);
  }
};

// Legacy support
export const triggerHaptic = hapticFeedback;

// Share functionality
export const shareRunbook = async (runbook) => {
  if (!isMobile()) {
    // Fallback for web
    if (navigator.share) {
      try {
        await navigator.share({
          title: runbook.title,
          text: runbook.description,
          url: window.location.href
        });
      } catch (error) {
        console.log('Web share cancelled or failed');
      }
    }
    return;
  }

  try {
    const { Share } = await import('@capacitor/share');
    await Share.share({
      title: runbook.title,
      text: `${runbook.description}\n\nSteps:\n${runbook.steps.join('\n')}`,
      url: window.location.href,
      dialogTitle: 'Share Runbook'
    });
  } catch (error) {
    console.error('Share error:', error);
  }
};

// Copy to clipboard
export const copyToClipboard = async (text) => {
  if (!isMobile()) {
    // Fallback for web
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Web clipboard error:', error);
      return false;
    }
  }

  try {
    const { Clipboard } = await import('@capacitor/clipboard');
    await Clipboard.write({ string: text });
    return true;
  } catch (error) {
    console.error('Mobile clipboard error:', error);
    return false;
  }
};

// Get network status
export const getNetworkStatus = async () => {
  if (!isMobile()) {
    return { connected: navigator.onLine, connectionType: 'unknown' };
  }

  try {
    const { Network } = await import('@capacitor/network');
    const status = await Network.getStatus();
    return status;
  } catch (error) {
    console.error('Network status error:', error);
    return { connected: false, connectionType: 'none' };
  }
};

// Device information
export const getDeviceInfo = async () => {
  if (!isMobile()) {
    return null;
  }

  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return info;
  } catch (error) {
    console.log('Device info not available:', error);
    return null;
  }
};

// Mobile-specific navigation
export const handleMobileNavigation = (action) => {
  // Trigger haptic feedback for navigation actions
  hapticFeedback('Light');
  
  // Add mobile-specific navigation logic here
  console.log('Mobile navigation:', action);
};

// Offline detection
export const isOffline = async () => {
  const status = await getNetworkStatus();
  return !status.connected;
};

// Mobile-optimized error handling
export const handleMobileError = (error, context) => {
  console.error(`Mobile error in ${context}:`, error);
  
  // Trigger haptic feedback for errors
  hapticFeedback('Heavy');
  
  // Add mobile-specific error handling
  return error.message || 'An unexpected error occurred.';
};

// Emergency call functionality (requires additional plugin)
export const makeEmergencyCall = async (phoneNumber) => {
  if (!isMobile()) {
    // Fallback for web
    window.open(`tel:${phoneNumber}`);
    return;
  }

  // Note: This requires @capacitor-community/call-number plugin
  // For now, use the web fallback
  window.open(`tel:${phoneNumber}`);
};

// App info
export const getAppInfo = async () => {
  if (!isMobile()) {
    return {
      name: 'Life Runbooks',
      version: '1.0.0',
      build: '1'
    };
  }

  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return info;
  } catch (error) {
    console.error('App info error:', error);
    return null;
  }
};